import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { LegalLinks } from "../components/LegalLinks";
import { BrandText } from "../components/ULoveULogo";
import { LangTextToggle } from "../components/common";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { useRegion } from "../context/RegionContext";
import { useAuth } from "../context/AuthContext";
import { useUnread } from "../context/UnreadContext";

function NotificationBell() {
  const { user } = useAuth();
  const { unread } = useUnread();
  const { t } = useLang();
  const navigate = useNavigate();
  if (!user) return null;
  const n = unread.total;
  return (
    <button
      onClick={() => navigate("/perfil", { state: { focus: "activity" } })}
      className="absolute left-3 top-safe-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/75 shadow-sm backdrop-blur-md transition-transform duration-200 hover:scale-105 active:scale-95"
      aria-label={t("activity.title")}
      data-testid="home-notification-bell"
    >
      <Bell className="h-[18px] w-[18px] text-[#0D47A1]" strokeWidth={2.2} />
      {n > 0 && (
        <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#E53935] px-1 text-[10px] font-extrabold leading-none text-white ring-2 ring-white" data-testid="home-notification-count">
          {n > 9 ? "9+" : n}
        </span>
      )}
    </button>
  );
}

function CategoryCard({ cat, index, onClick }) {
  const { lang } = useLang();
  return (
    <button
      onClick={onClick}
      className="group flex flex-col rounded-2xl border-[1.5px] border-navy bg-white p-1.5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(13,42,107,0.15)] active:scale-[0.97] fade-up"
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`category-card-${cat.key}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-mist">
        <img src={cat.image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {cat.adult && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-alert px-2 py-0.5 text-[10px] font-extrabold text-white shadow" data-testid="category-card-adult-badge">+18</span>
        )}
      </div>
      <span className="block truncate px-1 pb-0.5 pt-2 text-center text-[16px] font-bold leading-tight text-navy sm:text-[17px]">
        <BrandText text={lang === "en" ? cat.name_en : cat.name_es} />
      </span>
    </button>
  );
}

export default function HomeScreen() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { categories, guardCategory } = useAppData();
  const { region } = useRegion();
  const [scrolled, setScrolled] = useState(false);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white" data-testid="home-screen">
      <div className="relative shrink-0 bg-white" data-testid="home-hero">
        <img
          src={region.hero_image}
          alt={region.name}
          className="mx-auto block h-auto w-auto max-w-full"
          style={{ maxHeight: "max(140px, calc(100dvh - 330px))" }}
          data-testid="home-hero-img"
        />
        <NotificationBell />
        <LangTextToggle className="absolute right-3 top-safe-3 z-10" />
        <div className="pointer-events-none absolute inset-x-0 flex justify-center px-3 hero-title-pos">
          <h1 className="hero-title inline-block whitespace-nowrap pb-1.5 uppercase leading-none text-[clamp(1.35rem,7.6vw,3rem)]" data-testid="home-title">
            {region.brand_title}
          </h1>
        </div>
      </div>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col bg-white">
        <div className="mx-auto w-full max-w-xl shrink-0 px-[15px] pb-3 pt-3 text-center" data-testid="home-tagline">
          <p className={`[text-wrap:balance] font-black leading-tight tracking-tight text-[#E53935] ${lang === "en" ? "text-[clamp(1rem,5.8vw,2.3rem)]" : "text-[clamp(1rem,6.2vw,2.4rem)]"}`} data-testid="home-tagline-2">{t("brand.tagline2")}</p>
          <p className="mx-auto mt-1 max-w-md text-[15px] font-medium leading-snug text-[#0D47A1] sm:text-base" data-testid="home-tagline-1">{t("brand.tagline")}</p>
        </div>
        <div className="relative min-h-0 flex-1">
          <div
            className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-navy/10 to-transparent transition-opacity duration-200 ${scrolled ? "opacity-100" : "opacity-0"}`}
            data-testid="home-grid-shadow"
          />
          <div
            className="h-full overflow-y-auto overscroll-contain px-[15px] pb-16 pt-1 sm:px-6"
            onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 2)}
            data-testid="home-category-scroll"
          >
            <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3.5 lg:grid-cols-4" data-testid="home-category-grid">
              {categories.map((c, i) => (
                <CategoryCard key={c.key} cat={c} index={i} onClick={() => guardCategory(c.key, () => navigate(`/mercat?category=${c.key}`))} />
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-slate-400">{t("home.footer")}</p>
            <LegalLinks className="mt-2" testid="home-legal-links" />
          </div>
        </div>
      </div>
    </div>
  );
}
